import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useState } from "react";
import { TouchableOpacity } from "react-native";
import {
	Banner,
	type BannerProps,
	Button,
	Dialog,
	Text,
} from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import DialogWrapper from "../DialogWrapper";
import ScrollContainer from "../ScrollContainer";
import MobileHeader from "./MobileHeader";
import SavedMemoryItemMobile from "./SavedMemoryItemMobile";
import SavedMemoryItemWeb from "./SavedMemoryItemWeb";
import WebToolbar from "./WebToolbar";
type SavedMemoryProps = {
	banner: BannerProps;
	onBackPress: () => void;
	isWeb: boolean;
	onDelete: (selectedListItems: string[]) => void;
	ListSections: {
		title: string;
		icon: IconType;
		list: {
			text: string;
		}[];
	}[];
};
const SavedMemory = ({
	banner,
	ListSections,
	isWeb,
	onDelete,
	onBackPress,
}: SavedMemoryProps) => {
	const [selectedListItems, setSelectedListItems] = useState<string[]>([]);
	const [selectionMode, setSelectionMode] = useState<boolean>(false);
	const theme = useAppTheme();
	const totalItemsCount = ListSections.reduce(
		(acc, ListSection) => acc + ListSection.list.length,
		0,
	);
	const onItemLongPress = (item: { text: string }) => {
		if (selectedListItems.includes(item.text)) {
			if (selectedListItems.length === 1) {
				setSelectionMode(false);
			}
			setSelectedListItems(selectedListItems.filter((e) => e !== item.text));
		} else {
			setSelectionMode(true);
			setSelectedListItems([...selectedListItems, item.text]);
		}
	};
	const handleWebItemPress = (item: { text: string }) => {
		if (selectedListItems.includes(item.text)) {
			setSelectedListItems(selectedListItems.filter((e) => e !== item.text));
		} else {
			setSelectedListItems([...selectedListItems, item.text]);
		}
	};

	const handleMobileItemPress = (item: { text: string }) => {
		if (!selectionMode) {
			return;
		}

		if (selectedListItems.includes(item.text)) {
			if (selectedListItems.length === 1) {
				setSelectionMode(false);
			}
			setSelectedListItems(selectedListItems.filter((e) => e !== item.text));
		} else if (selectedListItems.length !== 0) {
			setSelectedListItems([...selectedListItems, item.text]);
		} else {
			setSelectionMode(false);
		}
	};

	const onItemPress = (item: { text: string }) => {
		if (isWeb) {
			handleWebItemPress(item);
		} else {
			handleMobileItemPress(item);
		}
	};
	const onSelectAll = () => {
		const allItems = ListSections.flatMap((section) =>
			section.list.map((item) => item.text),
		);
		const isAllSelected = selectedListItems.length === totalItemsCount;

		if (isWeb) {
			setSelectedListItems(isAllSelected ? [] : allItems);
			if (isAllSelected) {
				setSelectionMode(false);
			}
		} else if (selectedListItems.length > 0 && !isAllSelected) {
			setSelectedListItems(allItems);
		} else {
			setSelectionMode(false);
			setSelectedListItems([]);
		}
	};

	const SelectAllMobile = () => {
		return (
			<Box
				paddingHorizontal={8}
				gap={10}
				backgroundColor={theme.colors.surfaceVariant}
				flexDirection="row"
				alignItems="center"
			>
				<TouchableOpacity
					onPress={() => {
						onSelectAll();
					}}
				>
					<Box
						paddingVertical={10}
						paddingLeft={12}
						flexDirection="row"
						alignItems="center"
						gap={8}
					>
						<Icon
							name={
								selectedListItems.length === totalItemsCount
									? "checkbox-marked"
									: "checkbox-blank-outline"
							}
							size={24}
							color={theme.colors.primary}
						/>

						<Text variant="labelLarge" style={{ color: theme.colors.primary }}>
							{selectedListItems.length !== totalItemsCount
								? "Select all"
								: "Deselect all"}
						</Text>
					</Box>
				</TouchableOpacity>
			</Box>
		);
	};
	const content = (
		<ScrollContainer>
			{!isWeb && selectedListItems.length > 0 && <SelectAllMobile />}
			{!selectionMode && (
				<Banner
					{...banner}
					style={{ backgroundColor: theme.colors.surfaceVariant }}
					icon={({ size = 40 }) => (
						<Box
							borderRadius={100}
							width={size}
							height={size}
							justifyContent="center"
							alignItems="center"
							backgroundColor={theme.colors.secondary}
						>
							<Icon
								name={banner.icon as IconType}
								size={24}
								color={theme.colors.onSecondary}
							/>
						</Box>
					)}
				/>
			)}
			{isWeb && (
				<WebToolbar
					selectedListItems={selectedListItems}
					totalItemsCount={totalItemsCount}
					onSelectAll={onSelectAll}
					onClearSelection={() => {
						setSelectedListItems([]);
					}}
					onDelete={onDelete}
				/>
			)}
			<ScrollContainer>
				{ListSections.map((ListSection) => (
					<Box key={ListSection.title}>
						<Box flexDirection="row" padding={16} gap={8} alignItems="center">
							<Icon
								name={ListSection.icon as IconType}
								size={20}
								color={theme.colors.onSurfaceVariant}
							/>
							<Text variant="titleSmall">{ListSection.title}</Text>
						</Box>
						{ListSection.list.map((item) => (
							<Box key={item.text} paddingBottom={0}>
								{isWeb ? (
									<SavedMemoryItemWeb
										item={item}
										isSelected={selectedListItems.includes(item.text)}
										onPress={onItemPress}
										onDelete={onDelete}
									/>
								) : (
									<SavedMemoryItemMobile
										item={item}
										isSelected={selectedListItems.includes(item.text)}
										onPress={onItemPress}
										onLongPress={onItemLongPress}
									/>
								)}
							</Box>
						))}
					</Box>
				))}
			</ScrollContainer>
		</ScrollContainer>
	);

	if (isWeb) {
		return (
			<DialogWrapper visible={true} handleClose={onBackPress}>
				<Dialog.Title>Saved memories</Dialog.Title>

				{content}
				<Dialog.Actions>
					<Button mode="text" onPress={onBackPress}>
						Close
					</Button>
				</Dialog.Actions>
			</DialogWrapper>
		);
	}

	return (
		<Box flex={1} backgroundColor={theme.colors.surface}>
			<MobileHeader
				selectedCount={selectedListItems.length}
				onBackPress={onBackPress}
				onDelete={onDelete}
			/>
			{content}
		</Box>
	);
};

export default SavedMemory;
